/*
 * @Author: linguang linguang@nextmar.com
 * @Date: 2025-04-11 17:04:24
 * @LastEditors: Please set LastEditors
 * @LastEditTime: 2025-09-05 11:32:18
 * @FilePath: /src/components/Form/Checkbox/index.tsx
 * @Description: 该组件没有实际太多封装，只是方便查看主题色
 */
import { colors } from '@/theme/zeelool.theme';
import {
  FormControlLabel,
  FormHelperText,
  Checkbox as MuiCheckbox,
  CheckboxProps as MuiCheckboxProps,
  SxProps,
  Theme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Control, Controller, RegisterOptions } from 'react-hook-form';

/**
 * Checkbox component wrapper for MUI Checkbox
 *
 * @example
 * // 基本用法
 * <Checkbox label="同意条款" />
 *
 * // 使用 react-hook-form
 * <Checkbox
 *   name="agree"
 *   control={control}
 *   label="同意条款"
 *   rules={{ required: "请勾选同意条款" }}
 * />
 *
 * // 使用自定义图标
 * <Checkbox
 *   label="自定义图标"
 *   icon={<CustomUnCheckedIcon />}
 *   checkedIcon={<CustomCheckedIcon />}
 * />
 *
 * // 使用细线样式
 * <Checkbox
 *   label="细线样式"
 *   isThinner={true}
 *   fontSize={18}
 * />
 */
export interface CheckboxProps extends MuiCheckboxProps {
  /**
   * react-hook-form 字段名
   */
  name?: string;
  /**
   * react-hook-form 控制器
   */
  control?: Control<any>;
  /**
   * react-hook-form 验证规则
   */
  rules?: RegisterOptions;
  /**
   * 标签
   */
  label?: React.ReactNode;
  /**
   * 标签容器的自定义 className
   */
  labelClassName?: string;
  /**
   * 错误帮助文本
   */
  helperText?: string;

  /**
   * 自定义样式
   */
  sx?: SxProps<Theme>;

  /**
   * 是否使用圆形的checkbox icon
   * @default false
   */
  circle?: boolean;

  /**
   * 标签位置
   */
  labelPlacement?: 'start' | 'end' | 'top' | 'bottom';

  /**
   * 是否更细的checkbox
   * @default false
   */
  isThinner?: boolean;

  /**
   * icon 字体大小
   * @default 16
   */
  fontSize?: number;

  /**
   * 自定义未选中状态的图标
   * 如果传递了此值，将使用自定义图标而不是默认的图标逻辑
   */
  icon?: React.ReactNode;

  /**
   * 自定义选中状态的图标
   * 如果传递了此值，将使用自定义图标而不是默认的图标逻辑
   */
  checkedIcon?: React.ReactNode;
}

const BpIcon = styled('span')<{ fontSize?: number }>(({ fontSize = 16 }) => ({
  borderRadius: 3,
  width: fontSize,
  height: fontSize,
  border: '1px solid #d4d4d4',
  backgroundColor: '#ffffff',
  '.Mui-focusVisible &': {
    outline: `2px auto ${colors.primaryPurpleHover}`,
    outlineOffset: 2,
  },
  // 只在真正支持hover的设备上启用hover效果，避免移动端触摸后显示主题色边框
  '@media (hover: hover)': {
    'input:hover ~ &': {
      borderColor: colors.primaryBlackHover,
    },
  },
  'input:disabled ~ &': {
    borderColor: '#d4d4d4',
    backgroundColor: '#EEEEEE',
    opacity: 0.6,
  },
}));

const BpCheckedIcon = styled(BpIcon)<{ fontSize?: number }>(({ fontSize = 16 }) => ({
  backgroundColor: colors.primaryPurple,
  borderColor: colors.primaryPurple,
  position: 'relative',
  '&::before': {
    display: 'block',
    width: Math.max(fontSize * 0.9, 12), // 对勾大小为容器的90%，最小12px
    height: Math.max(fontSize * 0.9, 12),
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
      " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
      "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    content: '""',
  },
  // 只在真正支持hover的设备上启用hover效果，避免移动端触摸后显示主题色边框
  '@media (hover: hover)': {
    'input:hover ~ &': {
      backgroundColor: colors.primaryPurpleHover,
      borderColor: colors.primaryPurpleHover,
    },
  },
}));

const BpCircleIcon = styled(BpIcon)({
  borderRadius: '50%',
});

const BpCircleCheckedIcon = styled(BpCheckedIcon)({
  borderRadius: '50%',
});

const Checkbox = (props: CheckboxProps) => {
  const {
    name,
    control,
    rules,
    label,
    labelClassName,
    helperText,
    circle,
    sx,
    labelPlacement,
    fontSize = 16,
    icon: customIcon,
    checkedIcon: customCheckedIcon,
    isThinner = false,
    ...checkboxProps
  } = props;

  // 确定使用的图标：优先使用自定义图标，否则根据 isThinner 决定
  const getIcon = () => {
    if (customIcon !== undefined) {
      return customIcon;
    }
    if (circle) {
      return <BpCircleIcon fontSize={fontSize} />;
    }
    if (isThinner) {
      return <BpIcon fontSize={fontSize} />;
    }
    return undefined;
  };

  const getCheckedIcon = () => {
    if (customCheckedIcon !== undefined) {
      return customCheckedIcon;
    }
    if (circle) {
      return <BpCircleCheckedIcon fontSize={fontSize} />;
    }
    if (isThinner) {
      return <BpCheckedIcon fontSize={fontSize} />;
    }
    return undefined;
  };

  const iconToUse = getIcon();
  const checkedIconToUse = getCheckedIcon();

  // 如果提供了name和control，使用Controller
  if (name && control) {
    return (
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field, fieldState: { error } }) => (
          <>
            {label ? (
              <FormControlLabel
                control={
                  <MuiCheckbox
                    {...checkboxProps}
                    checked={!!field.value}
                    icon={iconToUse}
                    checkedIcon={checkedIconToUse}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      if (props.onChange) {
                        props.onChange(e, e.target.checked);
                      }
                    }}
                  />
                }
                sx={sx}
                label={label}
                labelPlacement={labelPlacement}
                className={labelClassName ?? (isThinner ? '!ml-[-9px]' : '')}
              />
            ) : (
              <MuiCheckbox
                {...checkboxProps}
                checked={!!field.value}
                icon={iconToUse}
                checkedIcon={checkedIconToUse}
                onChange={(e) => {
                  field.onChange(e.target.checked);
                  if (props.onChange) {
                    props.onChange(e, e.target.checked);
                  }
                }}
                sx={sx}
              />
            )}
            {error && <FormHelperText error>{error.message}</FormHelperText>}
            {!error && helperText && <FormHelperText>{helperText}</FormHelperText>}
          </>
        )}
      />
    );
  }

  // 如果有标签但没有使用 react-hook-form
  if (label) {
    return (
      <>
        <FormControlLabel
          control={
            <MuiCheckbox
              {...checkboxProps}
              name={name}
              icon={iconToUse}
              checkedIcon={checkedIconToUse}
            />
          }
          label={label}
          sx={sx}
          labelPlacement={labelPlacement}
          className={labelClassName ?? '!ml-[-9px]'}
        />
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </>
    );
  }

  // 否则返回原始组件
  return (
    <MuiCheckbox
      {...checkboxProps}
      name={name}
      sx={sx}
      icon={iconToUse}
      checkedIcon={checkedIconToUse}
    />
  );
};

export default Checkbox;
